import { Button } from "@d12labs/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@d12labs/ui/card";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { UserPlus } from "lucide-react";
import { useState } from "react";
// Shapes derive from the server's route defs (Phase 7) — no hand-typed Contact.
import { crmApi } from "../api";

export function ContactCreatePage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [company, setCompany] = useState("");
	const [notes, setNotes] = useState("");

	const createContact = useMutation({
		mutationFn: (payload: { name: string; email?: string; company?: string; notes?: string }) =>
			crmApi.createContact({ input: payload }),
		onSuccess: (contact) => {
			queryClient.invalidateQueries({ queryKey: ["crm", "contacts"] });
			navigate({ to: "/crm/contacts/$id", params: { id: contact.id } });
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (name.trim().length === 0) return;
		createContact.mutate({
			name: name.trim(),
			email: email.trim() || undefined,
			company: company.trim() || undefined,
			notes: notes.trim() || undefined,
		});
	};

	return (
		<>
			<PageHeader
				eyebrow="CONTACTS"
				icon={UserPlus}
				title="New contact"
				subtitle="Capture the basics — you can enrich later from the detail page."
				actions={
					<>
						<Link to="/crm">
							<Button variant="ghost">Cancel</Button>
						</Link>
						<Button type="submit" form="contact-create-form" disabled={createContact.isPending}>
							{createContact.isPending ? "Creating…" : "Create contact"}
						</Button>
					</>
				}
			/>

			<PageBody className="px-6 py-8">
				<div className="mx-auto max-w-2xl space-y-5">
					<form id="contact-create-form" onSubmit={handleSubmit} className="space-y-5">
						<Card>
							<CardHeader>
								<CardTitle>Identity</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="flex flex-col gap-1.5">
										<Label htmlFor="name">
											Name <span className="text-destructive">*</span>
										</Label>
										<Input
											id="name"
											value={name}
											onChange={(e) => setName(e.target.value)}
											placeholder="Jane Doe"
											required
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<Label htmlFor="email">Email</Label>
										<Input
											id="email"
											type="email"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											placeholder="jane@example.com"
										/>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Work</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="company">Company</Label>
									<Input
										id="company"
										value={company}
										onChange={(e) => setCompany(e.target.value)}
										placeholder="Acme Inc."
									/>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Notes</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="notes" className="sr-only">
										Notes
									</Label>
									<Textarea
										id="notes"
										value={notes}
										onChange={(e) => setNotes(e.target.value)}
										rows={4}
										placeholder="Context, referral source, anything useful…"
									/>
								</div>
							</CardContent>
						</Card>

						{createContact.error && (
							<p className="text-body-sm text-destructive">
								{(createContact.error as Error).message}
							</p>
						)}
					</form>

					{/* Sticky save bar */}
					<div className="sticky bottom-0 -mx-6 border-t border-hairline bg-canvas/95 backdrop-blur px-6 py-3 flex items-center justify-between">
						<span className="text-meta text-ink-subtle">
							{name.trim() ? "Ready to save" : "Fill in a name to continue"}
						</span>
						<div className="flex gap-2">
							<Link to="/crm">
								<Button variant="ghost">Discard</Button>
							</Link>
							<Button
								type="submit"
								form="contact-create-form"
								disabled={createContact.isPending || name.trim().length === 0}
							>
								{createContact.isPending ? "Creating…" : "Create contact"}
							</Button>
						</div>
					</div>
				</div>
			</PageBody>
		</>
	);
}
